import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Clock } from 'lucide-react';
import { getConfig, getApiUrl } from '../config';
import './RecentSearches.css';

const RecentSearches = ({ onSearchClick, onAuthorClick }) => {
  const config = getConfig();
  const [data, setData] = useState({ comment_searches: [], author_reports: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRecentSearches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/api/recent-searches'));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('Invalid recent searches response');
      }

      setData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentSearches();
  }, [fetchRecentSearches]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(config.ui.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleSearchClick = (search) => {
    if (onSearchClick) {
      const searchParams = {};
      if (search.author) {
        searchParams.author = search.author;
      }
      if (search.content_search) {
        searchParams.content_search = search.content_search;
      }
      try {
        const params = JSON.parse(search.search_params);
        searchParams.limit = params.limit || config.app.defaultLimit;
        searchParams.offset = params.offset || 0;
        onSearchClick(searchParams);
      } catch {
        searchParams.limit = config.app.defaultLimit;
        searchParams.offset = 0;
        onSearchClick(searchParams);
      }
    }
  };

  const handleAuthorClick = (author, commentsRange) => {
    if (onAuthorClick) {
      onAuthorClick(author, commentsRange);
    }
  };

  if (loading) {
    return (
      <div className="recent-searches-container">
        <div className="loading-recent">
          <div className="spinner"></div>
          <p>Загрузка последних поисков...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-searches-container">
        <div className="error-recent">
          <p>Ошибка загрузки: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-searches-container">
      <h2>Последние поиски</h2>
      
      <div className="recent-searches-grid">
        <div className="recent-searches-section">
          <h3>
            <Search size={20} />
            Поиски по комментариям
          </h3>
          {data.comment_searches && data.comment_searches.length > 0 ? (
            <ul className="recent-searches-list">
              {data.comment_searches.map((search, index) => (
                <li 
                  key={index} 
                  className="recent-search-item"
                  onClick={() => handleSearchClick(search)}
                >
                  <div className="search-item-content">
                    {search.author && (
                      <span className="search-author">Автор: {search.author}</span>
                    )}
                    {search.content_search && (
                      <span className="search-content">Текст: {search.content_search.substring(0, 50)}{search.content_search.length > 50 ? '...' : ''}</span>
                    )}
                    <div className="search-meta">
                      <span className="search-results">Найдено: {search.results_count}</span>
                      <span className="search-date">
                        <Clock size={14} />
                        {formatDate(search.created_at)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-recent-searches">Нет последних поисков</p>
          )}
        </div>

        <div className="recent-searches-section">
          <h3>
            <User size={20} />
            Досье на авторов
          </h3>
          {data.author_reports && data.author_reports.length > 0 ? (
            <ul className="recent-searches-list">
              {data.author_reports.map((report, index) => (
                <li 
                  key={index} 
                  className="recent-search-item"
                  onClick={() => handleAuthorClick(report.author, report.comments_range)}
                >
                  <div className="search-item-content">
                    <span className="search-author">
                      {report.author}
                      {report.years_list && (
                        <span className="years-list"> ({report.years_list})</span>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-recent-searches">Нет последних досье</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentSearches;
